import { Virtualizer } from '@tanstack/react-virtual';
import { useCallback, useRef } from 'react';

/**
 * A row virtualizer `measureElement` whose heights only ratchet up, the technique CardTableView
 * uses (useStableRowMeasure). Horizontal scrolling unmounts columns, so a row whose tallest cell
 * scrolled away would otherwise shrink, then grow again when it scrolls back. Rows are keyed by
 * their `data-row-id`.
 *
 * resetKey: a change that can make rows genuinely shorter (edited data, so new column widths) drops
 * the cache.
 */
export function useRatchetedRowMeasure(resetKey: unknown) {
  const rowHeightsRef = useRef<Map<string, number>>(new Map());
  const resetKeyRef = useRef(resetKey);

  if (resetKeyRef.current !== resetKey) {
    resetKeyRef.current = resetKey;
    rowHeightsRef.current.clear();
  }

  return useCallback(
    (
      row: HTMLDivElement,
      entry: ResizeObserverEntry | undefined,
      instance: Virtualizer<HTMLDivElement, HTMLDivElement>,
    ) => {
      const rowId = row.dataset.rowId ?? String(instance.indexFromElement(row));
      const cachedHeight = rowHeightsRef.current.get(rowId) ?? 0;

      // Re-apply the cached height before measuring, so cells unmounted by horizontal scroll can't
      // be read as a shrunken row. The cells stretch to the row's height.
      applyMinHeight(row, cachedHeight);

      const measuredHeight = Math.round(
        entry?.borderBoxSize?.[0]?.blockSize ?? row.getBoundingClientRect().height,
      );
      const stableHeight = Math.max(measuredHeight, cachedHeight);
      if (stableHeight > cachedHeight) {
        rowHeightsRef.current.set(rowId, stableHeight);
        applyMinHeight(row, stableHeight);
      }
      return stableHeight;
    },
    [],
  );
}

function applyMinHeight(row: HTMLDivElement, height: number) {
  row.style.minHeight = height > 0 ? `${height}px` : '';
}
